import React, { useEffect } from 'react';

import { useQuery } from '@apollo/client/react';

import {
  ChatType,
  CrmMetaTokenStatusFragment,
  CrmStoreDataFragment,
  CrmType,
} from '@anyx/common-graphql';
import { useWorkspaceCtx } from '@anyx/common-workspace';
import { LoadWrapper, NotFoundMessage, UnavailableMessage } from '@anyx/shared-ui';
import { reportAppError } from '@anyx/shared-utils';

import { AnyChatErrorCode, getAnyChatErrorCode } from '../../errors';
import {
  initCrmStoreState,
  setCrmStoreDetail,
  useCrmStore,
  CrmStoreLineChat,
  CrmStoreInstagramChat,
  CrmStoreMessengerChat,
  CrmStoreChats,
  CrmStore,
  CrmStoreLazadaChat,
  CrmStoreWhatsappChat,
  CrmStoreShopeeChat,
  CrmStoreTiktokShopChat,
} from '../../stores';

import { VALIDATE_CRM_STORE } from './crm-store-builder.graphql';

const resolveMetaTokenStatus = (tokenStatus: CrmMetaTokenStatusFragment) => {
  return tokenStatus.__typename !== 'MTS_WORKING';
};

type CrmStoreData = CrmStoreDataFragment;

const parseCrmStoreData = (data?: CrmStoreData | null): CrmStore => {
  if (!data) {
    throw new Error('Empty CrmStoreData');
  }

  return {
    storeId: data.id,
    storeName: data.name,
    isEnabled: data.isCrmEnabled,
    isInfluencerMode: data.crmType === CrmType.INFLUENCER,
    hasSlackNotifications: 'hasSlackNotifications' in data ? data.hasSlackNotifications : false,
    draftReplyEnabled: 'draftReplyEnabled' in data ? data.draftReplyEnabled : false,
    slackChannelId: 'slackChannelId' in data ? data.slackChannelId : null,
    saleChannels: data?.saleChannels?.map((saleChannel) => saleChannel.type) || [],
    chats: data.chats.reduce((acc, chat) => {
      switch (chat.__typename) {
        case 'LineStoreChat':
          return {
            ...acc,
            [ChatType.LINE]: {
              type: ChatType.LINE,
              id: chat.id,
              aiChatEnabled: chat.isAIReplyEnable,
              messageQuota: {
                limit: chat.messageQuota.limit || 0,
                consumption: chat.messageQuota.consumption,
              },
              disconnected: !chat.isRefreshTokenSuccess,
            } satisfies CrmStoreLineChat,
          } satisfies CrmStoreChats;
        case 'InstagramStoreChat':
          return {
            ...acc,
            [ChatType.INSTAGRAM]: {
              type: ChatType.INSTAGRAM,
              id: chat.id,
              aiChatEnabled: false,
              disconnected: resolveMetaTokenStatus(chat.tokenStatusV2),
            } satisfies CrmStoreInstagramChat,
          } satisfies CrmStoreChats;

        case 'MessengerStoreChat':
          return {
            ...acc,
            [ChatType.MESSENGER]: {
              type: ChatType.MESSENGER,
              id: chat.id,
              aiChatEnabled: false,
              disconnected: resolveMetaTokenStatus(chat.tokenStatusV2),
            } satisfies CrmStoreMessengerChat,
          } satisfies CrmStoreChats;

        case 'WhatsAppStoreChat':
          return {
            ...acc,
            [ChatType.WHATSAPP]: {
              type: ChatType.WHATSAPP,
              id: chat.id,
              aiChatEnabled: chat.isAIReplyEnable,
              disconnected: false,
              optInEnabled: chat.optInEnabled,
              name: chat.name,
            } satisfies CrmStoreWhatsappChat,
          } satisfies CrmStoreChats;

        case 'LazadaStoreChat':
          return {
            ...acc,
            [ChatType.LAZADA]: {
              type: ChatType.LAZADA,
              id: chat.id,
              aiChatEnabled: false,
              disconnected: !chat.tokenUpdatedSuccessfully,
            } satisfies CrmStoreLazadaChat,
          } satisfies CrmStoreChats;

        case 'ShopeeStoreChat':
          return {
            ...acc,
            [ChatType.SHOPEE]: {
              type: ChatType.SHOPEE,
              id: chat.id,
              aiChatEnabled: chat.isAIReplyEnable,
              disconnected: !chat.tokenUpdatedSuccessfully,
            } satisfies CrmStoreShopeeChat,
          } satisfies CrmStoreChats;

        case 'TikTokShopStoreChat':
          return {
            ...acc,
            [ChatType.TIKTOK_SHOP]: {
              type: ChatType.TIKTOK_SHOP,
              id: chat.id,
              aiChatEnabled: false,
              disconnected: false,
            } satisfies CrmStoreTiktokShopChat,
          } satisfies CrmStoreChats;

        default:
          return acc;
      }
    }, {} as CrmStoreChats),
  };
};

const useValidateCrmStore = ({ storeId }: { storeId?: string }) => {
  const { loading, error, data } = useQuery(VALIDATE_CRM_STORE, {
    fetchPolicy: 'network-only',
    skip: !storeId,
    variables: {
      input: {
        id: storeId as string,
      },
    },
  });

  useEffect(() => {
    if (data?.AnyChatStore) {
      const storeData = parseCrmStoreData(data.AnyChatStore as CrmStoreData);
      setCrmStoreDetail({ ...storeData });
    }
  }, [data?.AnyChatStore]);

  useEffect(() => {
    if (error) {
      reportAppError(error);
    }
  }, [error]);

  return { validating: loading || !storeId, error };
};

interface CrmStoreBuilderProps {
  children: React.ReactNode;
}

export const CrmStoreBuilder = ({ children }: CrmStoreBuilderProps) => {
  const { workspace } = useWorkspaceCtx();

  const { validating, error } = useValidateCrmStore({
    storeId: workspace?.storeId,
  });

  useEffect(() => {
    // Clean up crm store state when component unmounts
    return () => useCrmStore.setState(() => ({ ...initCrmStoreState }));
  }, []);

  if (validating) {
    return (
      <LoadWrapper
        loading={true}
        message="This is CRM Store Builder loader"
        loaderId="crm-store"
        className="h-screen w-full"
      />
    );
  }

  if (error) {
    const storeNotFound = getAnyChatErrorCode(error) === AnyChatErrorCode.STORE_NOT_FOUND;

    return (
      <div className="h-fit w-full p-8">
        {storeNotFound ? <NotFoundMessage /> : <UnavailableMessage />}
      </div>
    );
  }

  return <>{children}</>;
};
